/**
 * PATH: apps/quote/src/components/builder/phantom-row/usePhantomRowLogic.ts
 * PURPOSE: State management and event handlers for PhantomRow
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { quoteApi, type AISuggestion } from '@/lib/quoteApi'

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debouncedValue
}

const HARDCODED_DEFAULT_PMPD = '15'

/**
 * Build the phantom-row's initial values, layering quote-level config defaults
 * on top of hard-coded fallbacks. This is what makes "Setup" meaningful — the
 * estimator's per-quote pmpd default flows into every new row instead of every
 * row hard-coding 15 units/day.
 */
function makeDefaultValues(configDefaults?: Partial<{ defaultProductivityPmpd: number | string | null; defaultUnit: string }>) {
  const pmpd = configDefaults?.defaultProductivityPmpd
  const pmpdStr = pmpd !== null && pmpd !== undefined && String(pmpd).trim() !== ''
    ? String(pmpd)
    : HARDCODED_DEFAULT_PMPD
  return {
    location: '',
    description: '',
    quantity: '',
    unit: configDefaults?.defaultUnit || 'm2',
    productivity_pmpd: pmpdStr,
    labour_chargeout: '',
    material_chargeout: '',
    plant_chargeout: '',
    subcon_chargeout: '',
  }
}

interface UsePhantomRowLogicOptions {
  sectionId: string
  autoFocus: boolean
  onAddItem: (sectionId: string, data: {
    location?: string; description: string; quantity: number; unit: string;
    productivity_pmpd: number; labour_chargeout?: number | null; material_chargeout?: number | null;
    plant_chargeout?: number | null; subcon_chargeout?: number | null;
  }) => void
  /**
   * Optional defaults pulled from quote.config (set in the Setup tab). When
   * absent, the row falls back to HARDCODED_DEFAULT_PMPD (=15).
   */
  configDefaults?: { defaultProductivityPmpd?: number | string | null; defaultUnit?: string }
}

export function usePhantomRowLogic({ sectionId, autoFocus, onAddItem, configDefaults }: UsePhantomRowLogicOptions) {
  // Memoise the default values so they update when the user changes Setup
  // values (e.g. defaultProductivityPmpd in the AssumptionsSheet) — but stay
  // stable across unrelated re-renders.
  const defaultsKey = `${configDefaults?.defaultProductivityPmpd ?? ''}|${configDefaults?.defaultUnit ?? ''}`
  const DEFAULT_VALUES = useRef(makeDefaultValues(configDefaults)).current
  const lastDefaultsKey = useRef(defaultsKey)
  if (lastDefaultsKey.current !== defaultsKey) {
    lastDefaultsKey.current = defaultsKey
    Object.assign(DEFAULT_VALUES, makeDefaultValues(configDefaults))
  }
  const [values, setValues] = useState({ ...DEFAULT_VALUES })
  const [isActive, setIsActive] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)

  const descRef = useRef<HTMLInputElement>(null)
  const qtyRef = useRef<HTMLInputElement>(null)
  const unitRef = useRef<HTMLSelectElement>(null)
  const prodRef = useRef<HTMLInputElement>(null)
  const locRef = useRef<HTMLInputElement>(null)
  const labourRef = useRef<HTMLInputElement>(null)
  const materialRef = useRef<HTMLInputElement>(null)
  const plantRef = useRef<HTMLInputElement>(null)
  const subconRef = useRef<HTMLInputElement>(null)

  const debouncedDescription = useDebounce(values.description, 300)

  useEffect(() => {
    if (autoFocus && descRef.current) {
      requestAnimationFrame(() => descRef.current?.focus())
    }
  }, [autoFocus])

  const suggestionMutation = useMutation({
    mutationFn: (query: string) => quoteApi.getAISuggestions(query),
    onSuccess: (data) => { setSuggestions(data); setShowSuggestions(data.length > 0); setSelectedSuggestionIndex(-1) },
  })

  useEffect(() => {
    if (debouncedDescription.length >= 3 && isActive) {
      suggestionMutation.mutate(debouncedDescription)
    } else {
      setSuggestions([]); setShowSuggestions(false)
    }
  }, [debouncedDescription, isActive])

  const applySuggestion = useCallback((suggestion: AISuggestion) => {
    setValues(prev => ({
      ...prev,
      description: suggestion.item_name || suggestion.description || prev.description,
      unit: suggestion.unit || prev.unit,
    }))
    setShowSuggestions(false)
    quoteApi.recordAIFeedback(suggestion.id, 'accepted')
    setTimeout(() => qtyRef.current?.focus(), 50)
  }, [])

  const reset = useCallback(() => {
    setValues({ ...DEFAULT_VALUES }); setIsActive(false); setShowSuggestions(false); setSuggestions([])
  }, [])

  /**
   * Commit the phantom row.
   *
   * Why we accept empty descriptions:
   *   Excel doesn't validate. Estimators expect Enter to create a row they can
   *   fill in later. Silently bailing on empty description (the prior behaviour)
   *   manifested as "I can't press Enter" — Enter ran, the no-op happened, the
   *   user blamed the keyboard. See chat 2026-05-11 frustration thread.
   *
   *   The only thing we still bail on is "phantom is completely untouched"
   *   (no description AND no other field touched) so that someone tabbing
   *   through the grid doesn't accidentally commit a row of nothing.
   */
  const commit = useCallback(() => {
    const trimmedDesc = values.description.trim()
    const hasAnyValue =
      trimmedDesc.length > 0 ||
      values.location.trim().length > 0 ||
      values.quantity.trim().length > 0 ||
      values.labour_chargeout.trim().length > 0 ||
      values.material_chargeout.trim().length > 0 ||
      values.plant_chargeout.trim().length > 0 ||
      values.subcon_chargeout.trim().length > 0
    if (!hasAnyValue) {
      reset()
      return
    }
    const toOptionalNumber = (raw: string): number | null | undefined => {
      const trimmed = raw.trim()
      if (!trimmed) return undefined
      const n = Number.parseFloat(trimmed)
      return Number.isFinite(n) ? n : null
    }
    onAddItem(sectionId, {
      location: values.location || undefined,
      description: trimmedDesc,
      quantity: Number.parseFloat(values.quantity) || 1,
      unit: values.unit,
      productivity_pmpd: Number.parseFloat(values.productivity_pmpd) || 15,
      labour_chargeout: toOptionalNumber(values.labour_chargeout),
      material_chargeout: toOptionalNumber(values.material_chargeout),
      plant_chargeout: toOptionalNumber(values.plant_chargeout),
      subcon_chargeout: toOptionalNumber(values.subcon_chargeout),
    })
    setValues({ ...DEFAULT_VALUES }); setSuggestions([]); setShowSuggestions(false)
    setTimeout(() => descRef.current?.focus(), 50)
  }, [sectionId, values, onAddItem, reset])

  const refs = { descRef, qtyRef, unitRef, prodRef, locRef, labourRef, materialRef, plantRef, subconRef }

  const handleKeyDown = useCallback((e: React.KeyboardEvent, field: string) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedSuggestionIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1); return }
      if (e.key === 'Enter' && selectedSuggestionIndex >= 0) { e.preventDefault(); applySuggestion(suggestions[selectedSuggestionIndex]); return }
    }
    if (e.key === 'Enter' && !showSuggestions) { e.preventDefault(); commit(); return }
    if (e.key === 'Tab') {
      setShowSuggestions(false)
      const tabOrder = ['location', 'description', 'quantity', 'unit', 'productivity_pmpd', 'labour_chargeout', 'material_chargeout', 'plant_chargeout', 'subcon_chargeout']
      const refMap: Record<string, React.RefObject<HTMLElement | null>> = {
        location: locRef, description: descRef, quantity: qtyRef, unit: unitRef,
        productivity_pmpd: prodRef, labour_chargeout: labourRef, material_chargeout: materialRef,
        plant_chargeout: plantRef, subcon_chargeout: subconRef,
      }
      const currentIdx = tabOrder.indexOf(field)
      if (!e.shiftKey) {
        const nextIdx = currentIdx + 1
        if (nextIdx < tabOrder.length) { e.preventDefault(); refMap[tabOrder[nextIdx]]?.current?.focus() }
        else { e.preventDefault(); commit() }
      } else {
        const prevIdx = currentIdx - 1
        if (prevIdx >= 0) { e.preventDefault(); refMap[tabOrder[prevIdx]]?.current?.focus() }
      }
      return
    }
    if (e.key === 'Escape') reset()
  }, [showSuggestions, suggestions, selectedSuggestionIndex, applySuggestion, commit, reset])

  return {
    values, setValues, isActive, setIsActive, showSuggestions, setShowSuggestions,
    suggestions, selectedSuggestionIndex, setSelectedSuggestionIndex,
    refs, commit, handleKeyDown, applySuggestion, isFetchingSuggestions: suggestionMutation.isPending,
  }
}
